import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../../../core/services/api.service';

/** Input for getChargeListing GraphQL query */
export interface GetChargeListingInput {
  dosFrom?: string;
  dosTo?: string;
  accountNumber?: number;
  firstName?: string;
  lastName?: string;
  location?: string;
  provider?: string;
  plan?: string;
  planCategory?: string;
  cpt?: string;
  icd9?: string;
  enteredBy?: string;
  entryFrom?: string;
  entryTo?: string;
  submitDateFrom?: string;
  submitDateTo?: string;
  visitDateFrom?: string;
  visitDateTo?: string;
  practice?: string;
  /** e.g. PRIMARY, SECONDARY; omit or ALL to skip filter */
  chargeType?: string;
  /** Charge # / visit charge entry id filter; sent to API as numeric `chargeNumber`. */
  chargeNo?: string | number;
  /**
   * Payment status filter. Backend often expects enum-style values
   * (e.g. PAID, UNPAID, PARTIAL, PENDING).
   */
  paymentStatus?: string;
  page: number;
  limit: number;
}

/** Single item from getChargeListing data array */
export interface GetChargeListingDataItem {
  visitNo?: string;
  visitDosDate?: string;
  lastName?: string;
  firstName?: string;
  accountNo?: string;
  /** From backend (DiagnosisProcedure report mapping) */
  submitDate?: string | null;
  entryDate?: string;
  enteredBy?: string;
  dateOfBirth?: string;
  /** From backend: patient/account color id (nullable). */
  accountColor?: number | null;
  location?: string;
  provider?: string;
  plan?: string;
  cpt?: string;
  chargeAmt?: number;
  balance?: number;
  patientAmt?: number;
  planAmt?: number;
  patientBal?: number;
  planBal?: number;
  address?: string;
  homePhCell?: string;
  planId?: string | number;
  refProvider?: string;
  icd9Code?: string;
  modifier?: string;
  amtPaid?: number;
  adjustment?: number;
  planCategory?: string;
  writeOff?: number;
  patientPay?: number;
  copayPaid?: number;
  chargeDiscount?: number;
  copayDiscount?: number;
  copayBal?: number;
  visitChargeEntryId?: string | number;
  chargeIsTransferred?: boolean;
  planFollowUpAddInfo?: string;
}

/** GraphQL getChargeListing response shape */
export interface GetChargeListingResult {
  message?: string;
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  data?: GetChargeListingDataItem[];
}

export interface GetChargeHistoryInput {
  patientVisitId: number;
  visitChargeEntryId: number;
}

export interface ChargeHistoryItem {
  chargeHistoryId?: number;
  chargeHistoryDateTime?: string;
  chargeHistoryUserName?: string;
  chargeHistorySubmitType?: string;
  chargeHistorySubmitTypeName?: string;
  chargeHistoryPlanName?: string;
  chargeHistoryComments?: string;
  patientVisitId?: number;
  chargeHistoryChargeStatus?: string;
  visitChargeEntryId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ChargeListingService {

  constructor(private api: ApiService) {}

  /**
   * Charge Listing – GraphQL getChargeListing
   * GET charge listing with optional filters and pagination.
   */
  getChargeListing(input: GetChargeListingInput): Observable<{
    rows: GetChargeListingDataItem[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const query = `
      query ChargeListing($input: GetChargeListingInput!) {
        getChargeListing(input: $input) {
          message
          total
          page
          limit
          totalPages
          data {
            visitNo
            visitDosDate
            submitDate
            entryDate
            enteredBy
            accountNo
            firstName
            lastName
            location
            provider
            plan
            planCategory
            cpt
            modifier
            icd9Code
            chargeAmt
            planAmt
            patientAmt
            amtPaid
            writeOff
            chargeDiscount
            copayDiscount
            planBal
            patientBal
            copayBal
            balance
            visitChargeEntryId
            address
            homePhCell
            planId
            refProvider
            adjustment
            patientPay
            copayPaid
            chargeIsTransferred
            planFollowUpAddInfo
          }
        }
      }
    `;
    const variables = { input: this.buildInput(input) };
    return this.api.graphql<{ getChargeListing: GetChargeListingResult }>(query, variables).pipe(
      map((res: any) => {
        // Full GraphQL HTTP body may be passed when `data` is null (errors only).
        if (Array.isArray(res?.errors) && res.errors.length > 0) {
          const msg = res.errors.map((e: any) => e.message || String(e)).join('; ');
          throw new Error(msg || 'GraphQL error');
        }
        const list = res?.getChargeListing ?? res?.data?.getChargeListing;
        if (list == null) {
          return {
            rows: [] as GetChargeListingDataItem[],
            total: 0,
            page: input.page,
            limit: input.limit,
            totalPages: 0
          };
        }
        const data = list?.data ?? [];
        const rows = Array.isArray(data) ? data : [];
        const rawTotal = Number(list?.total);
        const total = Number.isFinite(rawTotal) ? rawTotal : rows.length;
        const rawLimit = Number(list?.limit);
        const limit = Number.isFinite(rawLimit) && rawLimit > 0 ? rawLimit : input.limit;
        const rawPage = Number(list?.page);
        const page = Number.isFinite(rawPage) ? rawPage : input.page;
        const rawTp = Number(list?.totalPages);
        const totalPages = Number.isFinite(rawTp) && rawTp >= 0
          ? rawTp
          : (limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1);
        return {
          rows,
          total,
          page,
          limit,
          totalPages
        };
      }),
      catchError((err) => throwError(() => err))
    );
  }

  getChargeHistory(input: GetChargeHistoryInput): Observable<ChargeHistoryItem[]> {
    const query = `
      query GetChargeHistory($input: GetChargeHistoryInput!) {
        getChargeHistory(input: $input) {
          data {
            chargeHistoryId
            chargeHistoryDateTime
            chargeHistoryUserName
            chargeHistorySubmitType
            chargeHistorySubmitTypeName
            chargeHistoryPlanName
            chargeHistoryComments
            patientVisitId
            chargeHistoryChargeStatus
            visitChargeEntryId
          }
        }
      }
    `;

    return this.api
      .graphql<{ getChargeHistory?: { data?: ChargeHistoryItem[] } }>(query, {
        input: {
          patientVisitId: Number(input.patientVisitId),
          visitChargeEntryId: Number(input.visitChargeEntryId)
        }
      })
      .pipe(
        map((res: any) => {
          if (Array.isArray(res?.errors) && res.errors.length > 0) {
            const msg = res.errors.map((e: any) => e.message || String(e)).join('; ');
            throw new Error(msg || 'GraphQL error');
          }
          const history = res?.getChargeHistory ?? res?.data?.getChargeHistory;
          const rows = history?.data ?? [];
          return Array.isArray(rows) ? rows : [];
        }),
        catchError((err) => throwError(() => err))
      );
  }

  private buildInput(input: GetChargeListingInput): Record<string, unknown> {
    const rawLimit = Math.floor(Number(input.limit));
    const limit = !Number.isFinite(rawLimit) || rawLimit <= 0 ? 999999 : Math.min(999999, Math.max(1, rawLimit));
    const out: Record<string, unknown> = {
      page: input.page,
      limit
    };
    const s = (v: string | null | undefined) => (v != null ? String(v).trim() : '');
    if (s(input.dosFrom)) out['dosFrom'] = s(input.dosFrom);
    if (s(input.dosTo)) out['dosTo'] = s(input.dosTo);
    if (input.accountNumber != null && input.accountNumber !== undefined) out['accountNumber'] = input.accountNumber;
    if (s(input.firstName)) out['firstName'] = s(input.firstName);
    if (s(input.lastName)) out['lastName'] = s(input.lastName);
    if (s(input.location)) out['location'] = s(input.location);
    if (s(input.provider)) out['provider'] = s(input.provider);
    if (s(input.plan)) out['plan'] = s(input.plan);
    if (s(input.planCategory)) out['planCategory'] = s(input.planCategory);
    if (s(input.cpt)) out['cpt'] = s(input.cpt);
    if (s(input.icd9)) out['icd9'] = s(input.icd9);
    if (s(input.enteredBy)) out['enteredBy'] = s(input.enteredBy);
    if (s(input.entryFrom)) out['entryFrom'] = s(input.entryFrom);
    if (s(input.entryTo)) out['entryTo'] = s(input.entryTo);
    if (s(input.submitDateFrom)) out['submitDateFrom'] = s(input.submitDateFrom);
    if (s(input.submitDateTo)) out['submitDateTo'] = s(input.submitDateTo);
    if (s(input.visitDateFrom)) out['visitDateFrom'] = s(input.visitDateFrom);
    if (s(input.visitDateTo)) out['visitDateTo'] = s(input.visitDateTo);
    if (s(input.practice)) out['practice'] = s(input.practice);
    const ct = s(input.chargeType).toUpperCase();
    if (ct !== '' && ct !== 'ALL') {
      out['chargeType'] = ct;
    }
    if (input.chargeNo != null && String(input.chargeNo).trim() !== '') {
      const chargeNumber = Number(String(input.chargeNo).trim());
      out['chargeNumber'] = Number.isFinite(chargeNumber) ? chargeNumber : String(input.chargeNo).trim();
    }

    const ps = input.paymentStatus != null ? String(input.paymentStatus).trim().toUpperCase() : '';
    if (ps !== '' && ps !== 'ALL') {
      out['paymentStatus'] = ps;
    }

    return out;
  }
}
